<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import BKImage from '@/components/AppShared/BKImage.vue'
import GmapCluster from 'vue2-google-maps/src/components/cluster'
import { type GmapMap, Marker, MarkerClusterer } from 'vue2-google-maps'
import { useVuetify } from '@/services/useVuetify'
import { MapCornersCoordinates, MappedMarkerCoordinates } from '@/enitites/map'
import { useDebounceFn } from '@vueuse/core'
import { SearchParams, Studio } from '@/modules/Studios/types'
import { StudioApi } from '@/enitites/studio'
import StudioMapChip from '@/modules/Studios/components/lists/StudioMapChip.vue'
import Bugsnag from '@bugsnag/js'
import { useImage } from '@/shared/composables/image'

const props = defineProps<{
  studios?: Studio[]
  keyword?: string
}>()

const vuetify = useVuetify()
const isDesktopTablet = computed(() => vuetify.breakpoint.smAndUp)
const { getStudioImage } = useImage()

// Map options
const MAP_OPTIONS = {
  // New York coordinates as default
  INIT_COORDINATES: { lat: 40.7128, lng: -74.006 },
  SHOW_ALL_STUDIOS_ZOOM: 1,
} as const

// Map refs
const map = ref<typeof GmapMap>(null)
const cluster = ref<typeof MarkerClusterer>(null)
const center = ref<{ lat: number; lng: number }>(MAP_OPTIONS.INIT_COORDINATES)
const visibleMapCoordinates = ref<MapCornersCoordinates>()

// Studios data
const fetchedStudios = ref<Studio[]>([])
const visibleStudios = ref<Studio[]>([])
const studioItems = ref<HTMLElement[] | null>(null)
const visibleStudioId = ref(0)
const hoveredStudioId = ref(0)
const isLoading = ref(false)
const error = ref<string | null>(null)
const MIN_VISIBLE_STUDIO_PERCENT = 60

// Get visible map coordinates
async function getVisibleMapCoordinates(): Promise<boolean> {
  try {
    if (!map.value) {
      return false
    }

    // Ensure Google Maps API is loaded
    if (map.value.$gmapApiPromiseLazy) {
      await map.value.$gmapApiPromiseLazy()
    } else {
      return false
    }

    // Get map bounds
    if (!map.value.$mapObject) {
      return false
    }

    const bounds = map.value.$mapObject.getBounds()
    if (!bounds) {
      return false
    }

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const se = new google.maps.LatLng(sw.lat(), ne.lng())
    const nw = new google.maps.LatLng(ne.lat(), sw.lng())

    visibleMapCoordinates.value = {
      nw: [nw.lat(), nw.lng()],
      ne: [ne.lat(), ne.lng()],
      se: [se.lat(), se.lng()],
      sw: [sw.lat(), sw.lng()],
    }

    return true
  } catch (e) {
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)), (event) => {
      event.addMetadata('map', {
        context: 'Error getting visible map coordinates',
      })
    })
    return false
  }
}

// Save map center coordinates to session storage
function saveMapCenterCoordinates() {
  sessionStorage.setItem('studio_lat', String(center.value.lat))
  sessionStorage.setItem('studio_lng', String(center.value.lng))
}

// Set user location
function setUserLocation(position: GeolocationPosition) {
  center.value = {
    lat: Number(sessionStorage.getItem('studio_lat')) || position.coords.latitude,
    lng: Number(sessionStorage.getItem('studio_lng')) || position.coords.longitude,
  }
}

// Check if studio element is in viewport
function checkElementInViewport() {
  if (!studioItems.value) return
  visibleStudios.value.forEach((studio, index: number) => {
    const element = studioItems.value?.[index]
    if (!element) return

    const rect = element.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowWidth = window.innerWidth || document.documentElement.clientWidth

    const vertInView = rect.top <= windowHeight && rect.top + rect.height >= 0
    const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0

    const visiblePercentage = vertInView && horInView ? 100 : 0

    if (visiblePercentage < MIN_VISIBLE_STUDIO_PERCENT && studio.id === visibleStudioId.value) {
      visibleStudioId.value = 0
      return
    }
    if (visiblePercentage >= MIN_VISIBLE_STUDIO_PERCENT) {
      visibleStudioId.value = studio.id
    }
  })
}

// Cluster styles
const CLUSTER_STYLES = [
  {
    textColor: 'var(--color-white)',
    url: '/img/ui-icons/selectedCluster.svg',
    height: 71,
    width: 76,
    textSize: 16,
  },
  {
    textColor: 'var(--color-white)',
    url: '/img/ui-icons/cluster.svg',
    height: 53,
    width: 58,
    textSize: 16,
  },
]

// Map options
const mapOptions = {
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: true,
  gestureHandling: 'greedy',
}

// Check if studio is active (visible or hovered)
function isActiveMapStudio(id: number) {
  return (id === visibleStudioId.value && !isDesktopTablet.value) || id === hoveredStudioId.value
}

// Throttled check for element in viewport
const throttledCheckElementInViewport = useDebounceFn(checkElementInViewport, 100)

// Computed properties for hovered and visible studios
const hoveredStudio = computed(() => visibleStudios.value.find((studio) => studio.id === hoveredStudioId.value))

// Check if hovered studio is in a cluster
function checkIsHoveredStudioInCluster(mappedMarkers: MappedMarkerCoordinates[]) {
  return mappedMarkers.some(
    (mappedMarker) =>
      mappedMarker.coordinates.lat === hoveredStudio.value?.locationLat &&
      mappedMarker.coordinates.lng === hoveredStudio.value?.locationLng
  )
}

// Calculate cluster styles
function calculateClusters(markers: (typeof Marker)[]) {
  // Check if markers is null or empty
  if (!markers || markers.length === 0) {
    return {
      index: 2,
      text: 0,
    }
  }

  const mappedMarkers = markers.map((marker) => ({
    coordinates: {
      lat: marker.position.lat(),
      lng: marker.position.lng(),
    },
  }))
  const hoveredStudioIsInCluster = checkIsHoveredStudioInCluster(mappedMarkers)

  if (hoveredStudioIsInCluster) {
    return {
      index: 1,
      text: markers.length,
    }
  }
  return {
    index: 2,
    text: markers.length,
  }
}

// Set visible studios to all studios
// The API already filters studios based on the visible map area
function filterVisibleStudios() {
  visibleStudios.value = fetchedStudios.value

  nextTick(() => {
    checkElementInViewport()
  })
}

// Fetch studios from API
async function fetchStudios() {
  try {
    isLoading.value = true
    error.value = null

    // Prepare search params
    const params: SearchParams = {
      pageNumber: 0,
      pageSize: 100, // Fetch a reasonable number of studios
    }

    // Add keyword if provided
    if (props.keyword) {
      params.keyword = props.keyword
    }

    // Check if visible map coordinates are available
    if (!visibleMapCoordinates.value) {
      // If no visible map coordinates, don't make the API call
      console.log('Skipping API call: visibleMap coordinates not available')
      return
    }

    // Add visible map coordinates
    params.visibleMap = visibleMapCoordinates.value

    // Add user location if available
    if (center.value) {
      params.userCoords = [center.value.lat, center.value.lng]
    }

    // Fetch studios from API
    fetchedStudios.value = await StudioApi.fetchStudiosMapList(params)

    // Filter visible studios
    filterVisibleStudios()
  } catch (err) {
    Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
      event.addMetadata('map', {
        context: 'Error fetching studios',
      })
    })
    error.value = 'Failed to load studios. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Handle map idle event
async function onIdle() {
  try {
    if (!map.value) {
      return
    }

    if (!map.value.$mapObject) {
      return
    }

    const mapCenter = map.value.$mapObject.getCenter()
    if (!mapCenter) {
      return
    }

    center.value = mapCenter.toJSON()
    saveMapCenterCoordinates()
    await getVisibleMapCoordinates()

    // If this is the first time the map is idle and we don't have studios yet,
    // fetch them now that we have visibleMapCoordinates
    if (!fetchedStudios.value || fetchedStudios.value.length === 0) {
      fetchStudios()
    }
  } catch (e) {
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)), (event) => {
      event.addMetadata('map', {
        context: 'Error in onIdle',
      })
    })
  }
}

// Initialize component
onMounted(() => {
  try {
    // Get user location if available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation(position)
          // Studios will be fetched in onIdle after visibleMapCoordinates is set
        },
        (e) => {
          Bugsnag.notify(new Error(e.message), (event) => {
            event.addMetadata('map', {
              context: 'Error getting geolocation',
            })
          })
          // Studios will be fetched in onIdle after visibleMapCoordinates is set
        },
        { enableHighAccuracy: true }
      )
    } else {
      // Studios will be fetched in onIdle after visibleMapCoordinates is set
    }

    // Check which studios are in viewport
    nextTick(() => {
      checkElementInViewport()

      // Force map to render by triggering a resize event
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('resize'))
        }
      }, 500)
    })
  } catch (e) {
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)), (event) => {
      event.addMetadata('map', {
        context: 'Error in onMounted',
      })
    })
  }
})

// Watch for changes in studios data
watch(fetchedStudios, () => {
  filterVisibleStudios()
})

// Watch for changes in visible map coordinates
// Use throttle to avoid too many API calls when panning the map
const throttledFetchStudios = useDebounceFn(fetchStudios, 1000)
watch(visibleMapCoordinates, () => {
  throttledFetchStudios()
})

// Watch for changes in keyword
watch(
  () => props.keyword,
  () => {
    if (visibleMapCoordinates.value) {
      throttledFetchStudios()
    }
  }
)
</script>

<template>
  <section
    aria-label="Studios map view mode"
    class="map-view"
  >
    <div class="studios-list">
      <div
        v-if="isLoading && (!visibleStudios || !visibleStudios.length)"
        class="loading-container"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
        <p class="loading-text">Loading studios...</p>
      </div>

      <div
        v-else-if="error"
        class="error-container"
      >
        <p class="error-text">{{ error }}</p>
        <v-btn
          color="primary"
          @click="fetchStudios"
        >
          Retry
        </v-btn>
      </div>

      <ul
        v-else-if="visibleStudios && visibleStudios.length"
        aria-label="Studios list"
        class="studios-list-items"
      >
        <li
          v-for="studio in visibleStudios"
          :key="studio.id"
          class="studios-item"
          @blur="hoveredStudioId = 0"
          @focus="hoveredStudioId = studio.id"
          @mouseleave="hoveredStudioId = 0"
          @mouseover="hoveredStudioId = studio.id"
        >
          <StudioMapChip :studio="studio" />
        </li>
      </ul>

      <p
        v-else
        class="no-results-msg"
      >
        No studios in this area
      </p>
    </div>
    <GmapMap
      ref="map"
      :center="center"
      :options="mapOptions"
      :zoom="5"
      class="map"
      style="height: 100%"
      @idle="onIdle"
    >
      <ul
        class="map-studio-list"
        @scroll="throttledCheckElementInViewport"
      >
        <li
          v-for="studio in visibleStudios || []"
          :key="studio.id"
          ref="studioItems"
        >
          <StudioMapChip :studio="studio" />
        </li>
      </ul>
      <GmapCluster
        ref="cluster"
        :calculator="calculateClusters"
        :grid-size="20"
        :styles="CLUSTER_STYLES"
        enable-retina-icons
        zoom-on-click
      >
        <template v-for="studio in visibleStudios || []">
          <GmapMarker
            v-if="studio && studio.locationLat && studio.locationLng"
            :key="studio.id"
            :clickable="true"
            :draggable="false"
            :opacity="0"
            :position="{ lat: studio.locationLat, lng: studio.locationLng }"
          >
            <GmapInfoWindow
              :options="{ pixelOffset: 60, maxWidth: 60, disableAutoPan: true }"
              :position="{ lat: studio.locationLat, lng: studio.locationLng }"
              :opened="true"
            >
              <router-link
                :class="['link', { active: isActiveMapStudio(studio.id) }]"
                :to="{ name: 'Studio', params: { id: studio.id } }"
              >
                <BKImage
                  :src="getStudioImage(studio.image, 84)"
                  class="img"
                  height="42"
                  width="42"
                />
              </router-link>
            </GmapInfoWindow>
          </GmapMarker>
        </template>
      </GmapCluster>
    </GmapMap>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

// stylelint-disable no-descending-specificity
.map-view {
  display: grid;
  grid-template-columns: 336px 1fr;
  gap: 20px;
  height: calc(100dvh - 218px);

  @media (max-width: 768px) {
    display: block;
  }

  .studios-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    @media (max-width: 768px) {
      @include visually-hidden;
    }

    .studios-list-items {
      display: flex;
      flex-direction: column;
      gap: 10px;
      height: 100%;
      padding: 20px 0 20px 20px;
      overflow-y: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .studio-card {
      display: flex;
      gap: 12px;
      padding: 12px;
      background-color: var(--color-white);
      border-radius: 8px;
      box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
      cursor: pointer;
      transition: transform 0.2s ease-in-out;

      &:hover {
        transform: translateY(-2px);
      }

      .studio-image {
        flex-shrink: 0;

        .img {
          object-fit: cover;
          border-radius: 8px;
        }
      }

      .studio-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
        overflow: hidden;

        .studio-name {
          margin: 0;
          font-weight: bold;
          font-size: 16px;
        }

        .studio-address {
          margin: 0;
          color: var(--color-grey-500);
          font-size: 14px;
        }

        .studio-description {
          margin: 0;
          overflow: hidden;
          font-size: 14px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    .no-results-msg {
      margin: 16px;
      text-align: center;
    }

    .loading-container,
    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      height: 200px;
    }

    .loading-text,
    .error-text {
      margin-top: 16px;
      text-align: center;
      color: var(--color-text-secondary);
    }
  }

  .link {
    display: flex;
    padding: 0;
    background-color: #fff;
    border-radius: 4px;
    cursor: pointer;
    transition: padding 0.2s ease-in-out;

    .img {
      border-radius: 4px;
      transition:
        width 0.2s ease-in-out,
        height 0.2s ease-in-out;
    }

    &:is(:hover, &.active) {
      padding: 4px;

      .img {
        width: 52px;
        height: 52px;
      }
    }
  }

  ::v-deep .cluster {
    div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  }

  .map {
    ::v-deep {
      .vue-map-hidden {
        display: flex;
        align-items: flex-end;
        height: 100%;
      }

      .map-studio-list {
        display: none;
        gap: 10px;
        align-items: flex-end;
        padding: 0 16px;
        overflow: auto;

        @media (max-width: 768px) {
          display: flex;
          padding: 30px 16px;
        }
      }
    }

    @media (max-width: 768px) {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.gm-ui-hover-effect {
  display: none !important;
}

.gm-style {
  .gm-style-iw-c {
    padding: 0 !important;
    background-color: transparent;
    border-radius: 4px;
    cursor: pointer;

    &:is(:focus-visible, :focus, :focus-within) {
      outline: none;
    }
  }

  .gm-style-iw-ch {
    padding: 0 !important;
  }

  .gm-style-iw-d {
    padding: 0;
    overflow: auto !important;
  }

  .gm-style-iw-tc::after {
    display: none;
  }
}
</style>
